/**
 * Post Card — flat design with border separator (no card wrapper).
 * Shows follow button for non-followed users.
 * Expandable text (4-5 lines → "Ko'proq o'qish").
 * Supports all post types including auto-posts.
 */

import { memo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Crown, CheckCircle2, UserPlus, Clock } from 'lucide-react'
import { triggerHapticNotification as hapticNotification } from '@turnifly/shared/utils'
import { useReportPost } from '@turnifly/shared/hooks'
import { formatDistanceToNow } from 'date-fns'
import { uz } from 'date-fns/locale'
import { useAuthStore } from '@turnifly/shared/stores'
import { useToggleLike, useDeletePost } from '@turnifly/shared/hooks'
import { PostActionsBar } from './post-actions-bar'
import { PostMenu } from './post-menu'
import { MediaCarousel } from './media-carousel'
import { StreakCard } from './auto-cards/streak-card'
import { ChallengeAutoCard } from './auto-cards/challenge-auto-card'
import { GoalAutoCard } from './auto-cards/goal-auto-card'
import type { FeedItem } from '@turnifly/shared/types'

type FollowState = 'following' | 'pending' | 'none'

interface PostCardProps {
  item: FeedItem
  followState: FollowState
  onCommentPress: (postId: string) => void
  onFollowPress: (userId: string) => void
  onRepostPress?: (postId: string) => void
}

const BADGE_ICON: Record<string, React.ReactNode> = {
  premium: <Crown size={11} className="text-primary" />,
  expert: <CheckCircle2 size={11} className="text-blue-400" />,
  organization: <CheckCircle2 size={11} className="text-emerald-400" />,
  company: <CheckCircle2 size={11} className="text-emerald-400" />,
}

const AUTO_LABEL: Record<string, string> = {
  streak_milestone: 'feed.auto.streakTitle',
  challenge_completed: 'feed.auto.challengeCompleted',
  challenge_joined: 'feed.auto.challengeJoined',
  goal_announced: 'feed.auto.goalAnnouncedTitle',
  goal_achieved: 'feed.auto.goalAchievedTitle',
  achievement_earned: 'feed.auto.achievementTitle',
}

export const PostCard = memo(function PostCard({
  item, followState, onCommentPress, onFollowPress, onRepostPress,
}: PostCardProps): React.ReactElement {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const userId = user?.id ?? ''
  const likeMutation = useToggleLike()
  const deleteMutation = useDeletePost()
  const reportMutation = useReportPost()

  const isOwn = item.author_id === userId
  const mediaUrls: string[] = Array.isArray(item.media_urls) ? item.media_urls : []
  const metadata = item.metadata ?? {}
  const isAutoPost = !!AUTO_LABEL[item.post_type]
  const showFollowBtn = !isOwn && followState === 'none'
  const showPendingBtn = !isOwn && followState === 'pending'

  const timeAgo = formatDistanceToNow(new Date(item.created_at), {
    addSuffix: false,
    locale: i18n.language === 'uz' ? uz : undefined,
  })

  const handleLike = useCallback((): void => {
    likeMutation.mutate({ postId: item.post_id, userId, isLiked: item.is_liked_by_me })
  }, [likeMutation, item.post_id, userId, item.is_liked_by_me])

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const handleDeleteRequest = useCallback((): void => {
    setShowDeleteConfirm(true)
  }, [])

  const handleDeleteConfirm = useCallback((): void => {
    setShowDeleteConfirm(false)
    deleteMutation.mutate(item.post_id)
  }, [deleteMutation, item.post_id])

  const handleReport = useCallback((): void => {
    if (!userId) return
    reportMutation.mutate(
      { postId: item.post_id, userId, reason: 'inappropriate' },
      {
        onSuccess: () => hapticNotification('success'),
      },
    )
  }, [reportMutation, item.post_id, userId])

  const handleRepost = useCallback((): void => {
    onRepostPress?.(item.post_id)
  }, [onRepostPress, item.post_id])

  const handleShare = useCallback((): void => {
    // Web share link — opens Telegram share dialog in a new tab.
    // Works identically in browser, PWA, and Capacitor since the
    // t.me/share/url URL is a public web endpoint.
    const link = `https://t.me/TurniflyBot/app?startapp=post_${item.post_id}`
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(link)}`
    // Use the Web Share API where available for a native-feeling
    // sheet on mobile; otherwise fall back to opening the share link.
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      void navigator.share({ url: link }).catch(() => {
        window.open(shareUrl, '_blank')
      })
    } else {
      window.open(shareUrl, '_blank')
    }
  }, [item.post_id])

  return (
    <article className="border-b border-white/[0.06]" data-post-id={item.post_id}>
      <div className="px-4 pt-4 pb-3">
        {/* Header: avatar + name + badge + time + follow/menu */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/profile/${item.author_id}`)}
            className="shrink-0 w-10 h-10 rounded-full overflow-hidden bg-white/[0.06] ring-1 ring-white/[0.06] tap-highlight-transparent"
          >
            {item.author_avatar ? (
              <img src={item.author_avatar} alt="" className="w-full h-full object-cover" loading="lazy" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[15px] font-semibold text-text-hint/50">
                {(item.author_name ?? '?')[0]}
              </div>
            )}
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => navigate(`/profile/${item.author_id}`)}
                className="text-[14px] font-semibold text-text truncate tap-highlight-transparent hover:underline"
              >
                {item.author_name}
              </button>
              {item.author_badge && BADGE_ICON[item.author_badge]}
            </div>
            <span className="text-[11px] text-text-hint/35">{timeAgo}</span>
          </div>

          {/* Follow / Pending button */}
          {showFollowBtn && (
            <button
              onClick={() => onFollowPress(item.author_id)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-[12px] font-semibold hover:bg-primary/20 transition-colors tap-highlight-transparent"
            >
              <UserPlus size={13} />
              {t('feed.follow')}
            </button>
          )}
          {showPendingBtn && (
            <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/[0.04] text-text-hint/40 text-[12px] font-medium">
              <Clock size={12} />
              {t('feed.pending')}
            </div>
          )}

          <PostMenu
            isOwn={isOwn}
            onDelete={isOwn ? handleDeleteRequest : undefined}
            onReport={!isOwn ? handleReport : undefined}
            onShare={handleShare}
          />
        </div>

        {/* Auto-post label */}
        {isAutoPost && (
          <p className="text-[11px] font-semibold text-text-hint/50 mt-3 tracking-wide uppercase">
            {t(AUTO_LABEL[item.post_type])}
          </p>
        )}

        {/* Text content — 4-5 lines then "Ko'proq o'qish" */}
        {item.content && (
          <div className={isAutoPost ? 'mt-1' : 'mt-3'}>
            <ExpandableText text={item.content} />
          </div>
        )}

        {/* Auto-cards */}
        {item.post_type === 'streak_milestone' && (
          <div className="mt-3">
            <StreakCard metadata={metadata as Record<string, number>} />
          </div>
        )}

        {(item.post_type === 'challenge_joined' || item.post_type === 'challenge_completed') && (
          <div className="mt-3">
            <ChallengeAutoCard
              postType={item.post_type as 'challenge_joined' | 'challenge_completed'}
              challengeId={null}
              metadata={metadata as Record<string, unknown>}
            />
          </div>
        )}

        {(item.post_type === 'goal_announced' || item.post_type === 'goal_achieved') && (
          <div className="mt-3">
            <GoalAutoCard
              postType={item.post_type as 'goal_announced' | 'goal_achieved'}
              metadata={metadata as Record<string, unknown>}
            />
          </div>
        )}

        {item.post_type === 'achievement_earned' && (
          <div className="mt-3 rounded-2xl bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border border-amber-500/15 p-4 flex items-center gap-3.5">
            <span className="text-3xl">{String(metadata.achievement_icon ?? '🏅')}</span>
            <div>
              <p className="text-[14px] font-semibold text-text">{String(metadata.achievement_name ?? '')}</p>
              <p className="text-[11px] text-text-hint/50 mt-0.5">{t('feed.auto.newAchievement')}</p>
            </div>
          </div>
        )}

        {/* Repost — original post nested, tappable to author profile */}
        {item.post_type === 'repost' && item.original_post && (
          <button
            onClick={() => {
              const op = item.original_post as Record<string, unknown> | undefined
              const authorId = op?.author_id as string | undefined
              const originalId = op?.id as string | undefined
              if (authorId && originalId) {
                navigate(`/profile/${authorId}?post=${originalId}`)
              } else if (authorId) {
                navigate(`/profile/${authorId}`)
              }
            }}
            className="mt-3 border border-white/[0.06] rounded-xl p-3 w-full text-left tap-highlight-transparent hover:bg-white/[0.02] transition-colors"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full overflow-hidden bg-white/[0.06]">
                {item.original_post.author_avatar ? (
                  <img src={item.original_post.author_avatar} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] text-text-hint/50">
                    {(item.original_post.author_name ?? '?')[0]}
                  </div>
                )}
              </div>
              <span className="text-[12px] font-semibold text-text-hint/70">{item.original_post.author_name}</span>
            </div>
            {item.original_post.content && (
              <p className="text-[13px] text-text/70 line-clamp-3">{item.original_post.content}</p>
            )}
            {item.original_post.media_urls && (item.original_post.media_urls as string[]).length > 0 && (
              <div className="mt-2">
                <MediaCarousel urls={item.original_post.media_urls as string[]} />
              </div>
            )}
          </button>
        )}

        {/* Media carousel */}
        {mediaUrls.length > 0 && item.post_type !== 'repost' && (
          <div className="mt-3">
            <MediaCarousel urls={mediaUrls} />
          </div>
        )}

        {/* Actions */}
        <div className="mt-2">
          <PostActionsBar
            likesCount={item.likes_count}
            commentsCount={item.comments_count}
            repostsCount={item.reposts_count}
            viewsCount={item.views_count}
            isLiked={item.is_liked_by_me}
            onLike={handleLike}
            onComment={() => onCommentPress(item.post_id)}
            onRepost={handleRepost}
            onShare={handleShare}
          />
        </div>
      </div>

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <>
          <div className="fixed inset-0 bg-black/60 z-[9998]" onClick={() => setShowDeleteConfirm(false)} />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] bg-[var(--color-bg)] border border-white/[0.08] rounded-2xl p-5 w-[280px] text-center shadow-2xl">
            <p className="text-[15px] font-semibold text-text mb-1">{t('feed.menu.delete')}</p>
            <p className="text-[13px] text-text-hint/50 mb-5">{t('feed.menu.deleteConfirm')}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2.5 rounded-xl bg-white/[0.06] text-[13px] font-medium text-text tap-highlight-transparent"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="flex-1 py-2.5 rounded-xl bg-red-500/15 text-[13px] font-medium text-red-400 tap-highlight-transparent"
              >
                {t('feed.menu.delete')}
              </button>
            </div>
          </div>
        </>
      )}
    </article>
  )
})

// ── Expandable Text — 4-5 lines then expand ──

const TEXT_LINE_LIMIT = 5

function ExpandableText({ text }: { text: string }): React.ReactElement {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const isLong = text.length > 250 || text.split('\n').length > TEXT_LINE_LIMIT

  return (
    <div>
      <p className={`text-[14px] text-text/90 leading-[1.65] whitespace-pre-wrap break-words ${
        !expanded && isLong ? 'line-clamp-5' : ''
      }`}>
        {text}
      </p>
      {isLong && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="text-[13px] text-primary/80 font-medium mt-1 tap-highlight-transparent hover:text-primary transition-colors"
        >
          {t('feed.showMore')}
        </button>
      )}
    </div>
  )
}
